import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  TextareaAutosize,
} from '@mui/material';
import {
  ChatCircleText,
  FileText,
  Microphone,
  Plus,
  TrashSimple,
  CloudArrowUp,
  X,
} from '@phosphor-icons/react';
import { useRef } from 'react';

interface Scenario {
  id: number;
  type: 'use_case' | 'call_recording' | 'transcript';
  title: string;
  content: string | string[];
  description: string;
  file?: File; // Add file property for call recordings
}

interface PromptConfigurationProps {
  setScenarios: (scenarios: Scenario[]) => void;
  scenarios: Scenario[];
  templatePrompt: string;
  setTemplatePrompt: (templatePrompt: string) => void;
}

const PromptConfiguration: React.FC<PromptConfigurationProps> = ({
  setScenarios,
  scenarios,
  templatePrompt,
  setTemplatePrompt,
}) => {
  const scenarioTypes = [
    { value: 'use_case', label: 'Use Case', icon: FileText, disabled: false },
    {
      value: 'call_recording',
      label: 'Call Recording',
      icon: Microphone,
      disabled: true,
    },
    {
      value: 'transcript',
      label: 'Conversation Transcript',
      icon: ChatCircleText,
      disabled: true,
    },
  ];

  const addScenario = (type: 'use_case' | 'call_recording' | 'transcript') => {
    // Only allow adding if the scenario type is not disabled
    const scenarioType = scenarioTypes.find(st => st.value === type);
    if (scenarioType?.disabled) {
      return; // Don't add scenario if disabled
    }

    const newScenario: Scenario = {
      id: Date.now(),
      type,
      title: '',
      content: type === 'transcript' ? [''] : '',
      description: '',
    };
    setScenarios([...scenarios, newScenario]);
  };

  const removeScenario = (id: number) => {
    setScenarios(scenarios.filter(s => s.id !== id));
  };

  const updateScenario = (id: number, field: keyof Scenario, value: any) => {
    setScenarios(
      scenarios.map(s => (s.id === id ? { ...s, [field]: value } : s))
    );
  };

  const addTranscriptLine = (scenarioId: number) => {
    setScenarios(
      scenarios.map(s =>
        s.id === scenarioId && s.type === 'transcript'
          ? { ...s, content: [...(s.content as string[]), ''] }
          : s
      )
    );
  };

  const updateTranscriptLine = (
    scenarioId: number,
    lineIndex: number,
    value: string
  ) => {
    setScenarios(
      scenarios.map(s =>
        s.id === scenarioId && s.type === 'transcript'
          ? {
              ...s,
              content: (s.content as string[]).map((line, idx) =>
                idx === lineIndex ? value : line
              ),
            }
          : s
      )
    );
  };

  const removeTranscriptLine = (scenarioId: number, lineIndex: number) => {
    setScenarios(
      scenarios.map(s =>
        s.id === scenarioId && s.type === 'transcript'
          ? {
              ...s,
              content: (s.content as string[]).filter(
                (_, idx) => idx !== lineIndex
              ),
            }
          : s
      )
    );
  };

  // File upload component for call recordings
  const CallRecordingUpload = ({ scenario }: { scenario: Scenario }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileUpload = () => {
      fileInputRef.current?.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        // Validate file type
        if (
          !file.type.startsWith('audio/') &&
          !file.name.toLowerCase().endsWith('.mp3')
        ) {
          alert('Please select a valid MP3 audio file.');
          return;
        }

        // Update scenario with file and set content to file name
        updateScenario(scenario.id, 'file', file);
        updateScenario(scenario.id, 'content', file.name);
      }
    };

    const handleRemoveFile = () => {
      updateScenario(scenario.id, 'file', undefined);
      updateScenario(scenario.id, 'content', '');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    return (
      <Box>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".mp3,audio/*"
          style={{ display: 'none' }}
        />

        {!scenario.file ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px dashed #DCE0E7',
              borderRadius: '8px',
              p: 3,
              cursor: 'pointer',
              backgroundColor: '#FAFBFC',
              '&:hover': {
                backgroundColor: '#F5F6F7',
                borderColor: '#5A4CE6',
              },
            }}
            onClick={handleFileUpload}
          >
            <CloudArrowUp
              size={32}
              color="#6B6F80"
              style={{ marginBottom: '8px' }}
            />
            <Typography
              variant="body2"
              sx={{
                color: '#6B6F80',
                textAlign: 'center',
                mb: 1,
              }}
            >
              Upload MP3 audio file
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#9CA3AF',
                textAlign: 'center',
              }}
            >
              Click to browse or drag and drop
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              border: '1px solid #DCE0E7',
              borderRadius: '8px',
              backgroundColor: '#F8F9FC',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Microphone size={20} color="#5A4CE6" />
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: '#302B3F',
                  }}
                >
                  {scenario.file.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#6B6F80',
                  }}
                >
                  {(scenario.file.size / (1024 * 1024)).toFixed(2)} MB
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={handleRemoveFile}
              size="small"
              sx={{
                color: '#E74C3C',
                backgroundColor: '#E74C3C26',
                '&:hover': {
                  backgroundColor: '#E74C3C33',
                },
              }}
            >
              <X size={16} />
            </IconButton>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        overflow: 'auto',
        flexGrow: 1,
        px: 3,
        pb: 3,
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Template Prompt */}
        <Box sx={{ border: 'none' }}>
          <Box>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: 14,
                color: '#302B3F',
                mb: 2,
              }}
            >
              Template Prompt
            </Typography>
            <TextareaAutosize
              value={templatePrompt}
              onChange={e => setTemplatePrompt(e.target.value)}
              minRows={3}
              maxRows={10}
              placeholder="Enter your template prompt here (JSON format or plain text)..."
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                border: '1px solid #DCE0E7',
                borderRadius: '6px',
                fontFamily: 'inherit',
                fontWeight: 400,
                lineHeight: '1.5',
                letterSpacing: '0.01em',
                color: '#302B3F',
                backgroundColor: '#FFFFFF',
                resize: 'none',
                outline: 'none',
                transition: 'border-color 0.2s ease',
              }}
              onFocus={e => {
                e.target.style.borderColor = '#5A4CE6';
              }}
              onBlur={e => {
                e.target.style.borderColor = '#DCE0E7';
              }}
            />
          </Box>
        </Box>

        {/* Scenarios Section */}
        <Box sx={{ border: 'none' }}>
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: 14,
                    color: '#302B3F',
                  }}
                >
                  Scenarios
                </Typography>
              </Box>

              {/* Add Scenario Buttons */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {scenarioTypes.map(({ value, label, icon: Icon, disabled }) => (
                  <Button
                    key={value}
                    onClick={() => !disabled && addScenario(value as any)}
                    variant="outlined"
                    size="small"
                    disabled={disabled}
                    startIcon={<Icon size={16} />}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      minWidth: 'auto',
                      px: 1.5,
                      ...(disabled && {
                        color: '#9CA3AF',
                        borderColor: '#E5E7EB',
                        backgroundColor: '#F9FAFB',
                        cursor: 'not-allowed',
                        '&:hover': {
                          borderColor: '#E5E7EB',
                          backgroundColor: '#F9FAFB',
                        },
                      }),
                    }}
                    title={disabled ? 'Coming soon' : undefined}
                  >
                    {label}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* Scenarios List */}
            {scenarios.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 4,
                  color: 'lightGray.text',
                }}
              >
                <ChatCircleText
                  size={48}
                  style={{ opacity: 0.5, marginBottom: '16px' }}
                />
                <Typography variant="body2">
                  No scenarios added yet. Click the buttons above to add
                  scenarios.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {scenarios.map(scenario => {
                  const ScenarioIcon =
                    scenarioTypes.find(t => t.value === scenario.type)?.icon ||
                    FileText;

                  return (
                    <Box
                      key={scenario.id}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '8px',
                        p: 2,
                        backgroundColor: '#FFFFFF',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <ScenarioIcon size={20} color="#5A4CE6" />
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: 14,
                              color: '#302B3F',
                              textTransform: 'capitalize',
                            }}
                          >
                            {scenario.type.replace('_', ' ')}
                          </Typography>
                        </Box>
                        <IconButton
                          onClick={() => removeScenario(scenario.id)}
                          size="small"
                          sx={{ color: 'error.main' }}
                        >
                          <TrashSimple size={16} />
                        </IconButton>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}
                      >
                        <TextField
                          label="Title"
                          value={scenario.title}
                          onChange={e =>
                            updateScenario(scenario.id, 'title', e.target.value)
                          }
                          fullWidth
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: '6px',
                              backgroundColor: '#FFFFFF',
                              '& fieldset': {
                                borderWidth: '1px',
                                borderColor: '#DCE0E7',
                                '&:hover': {
                                  borderColor: '#DCE0E7',
                                },
                              },
                            },
                            '& .MuiOutlinedInput-input': {
                              fontWeight: 400,
                              fontSize: '14px',
                              lineHeight: '100%',
                              letterSpacing: '-1%',
                              color: '#302B3F',
                              backgroundColor: '#FFFFFF',
                              '&::placeholder': {
                                color: '#6B6F80',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              fontWeight: 400,
                              fontSize: '12px',
                              color: '#6B6F80',
                            },
                          }}
                        />

                        {scenario.type === 'call_recording' ? (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                fontSize: 14,
                                color: '#302B3F',
                                mb: 1,
                              }}
                            >
                              Audio File
                            </Typography>
                            <CallRecordingUpload scenario={scenario} />
                          </Box>
                        ) : scenario.type === 'transcript' ? (
                          <Box>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  fontSize: 14,
                                  color: '#302B3F',
                                }}
                              >
                                Conversation Lines
                              </Typography>
                              <Button
                                onClick={() => addTranscriptLine(scenario.id)}
                                size="small"
                                startIcon={<Plus size={12} />}
                                sx={{
                                  textTransform: 'none',
                                  fontSize: '12px',
                                  minWidth: 'auto',
                                  px: 1,
                                }}
                              >
                                Add Line
                              </Button>
                            </Box>

                            {(scenario.content as string[]).map(
                              (line, lineIndex) => (
                                <Box
                                  key={lineIndex}
                                  sx={{ display: 'flex', gap: 1, mb: 1 }}
                                >
                                  <TextField
                                    value={line}
                                    onChange={e =>
                                      updateTranscriptLine(
                                        scenario.id,
                                        lineIndex,
                                        e.target.value
                                      )
                                    }
                                    multiline
                                    rows={2}
                                    fullWidth
                                    size="small"
                                    placeholder={`Conversation line ${lineIndex + 1}...`}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        backgroundColor: '#FFFFFF',
                                        '& fieldset': {
                                          borderWidth: '1px',
                                          borderColor: '#DCE0E7',
                                          '&:hover': {
                                            borderColor: '#DCE0E7',
                                          },
                                        },
                                      },
                                      '& .MuiOutlinedInput-input': {
                                        fontWeight: 400,
                                        fontSize: '14px',
                                        lineHeight: '100%',
                                        letterSpacing: '-1%',
                                        color: '#302B3F',
                                        backgroundColor: '#FFFFFF',
                                        '&::placeholder': {
                                          color: '#6B6F80',
                                        },
                                      },
                                    }}
                                  />
                                  {(scenario.content as string[]).length >
                                    1 && (
                                    <IconButton
                                      onClick={() =>
                                        removeTranscriptLine(
                                          scenario.id,
                                          lineIndex
                                        )
                                      }
                                      size="small"
                                      sx={{ color: 'error.main' }}
                                    >
                                      <TrashSimple size={16} />
                                    </IconButton>
                                  )}
                                </Box>
                              )
                            )}
                          </Box>
                        ) : (
                          <TextField
                            label="Content"
                            value={scenario.content as string}
                            onChange={e =>
                              updateScenario(
                                scenario.id,
                                'content',
                                e.target.value
                              )
                            }
                            multiline
                            rows={4}
                            fullWidth
                            placeholder="Enter scenario content..."
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                backgroundColor: '#FFFFFF',
                                '& fieldset': {
                                  borderWidth: '1px',
                                  borderColor: '#DCE0E7',
                                  '&:hover': {
                                    borderColor: '#DCE0E7',
                                  },
                                },
                              },
                              '& .MuiOutlinedInput-input': {
                                fontWeight: 400,
                                fontSize: '14px',
                                lineHeight: '100%',
                                letterSpacing: '-1%',
                                color: '#302B3F',
                                backgroundColor: '#FFFFFF',
                                '&::placeholder': {
                                  color: '#6B6F80',
                                },
                              },
                              '& .MuiInputLabel-root': {
                                fontWeight: 400,
                                fontSize: '12px',
                                color: '#6B6F80',
                              },
                            }}
                          />
                        )}

                        {/* Only show description for call_recording and transcript, make it required */}
                        {scenario.type !== 'use_case' && (
                          <TextField
                            label="Description"
                            value={scenario.description}
                            onChange={e =>
                              updateScenario(
                                scenario.id,
                                'description',
                                e.target.value
                              )
                            }
                            multiline
                            rows={2}
                            fullWidth
                            required
                            placeholder="Enter description for this scenario..."
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                backgroundColor: '#FFFFFF',
                                '& fieldset': {
                                  borderWidth: '1px',
                                  borderColor: '#DCE0E7',
                                  '&:hover': {
                                    borderColor: '#DCE0E7',
                                  },
                                },
                              },
                              '& .MuiOutlinedInput-input': {
                                fontWeight: 400,
                                fontSize: '14px',
                                lineHeight: '100%',
                                letterSpacing: '-1%',
                                color: '#302B3F',
                                backgroundColor: '#FFFFFF',
                                '&::placeholder': {
                                  color: '#6B6F80',
                                },
                              },
                              '& .MuiInputLabel-root': {
                                fontWeight: 400,
                                fontSize: '12px',
                                color: '#6B6F80',
                              },
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PromptConfiguration;
